import React, { Ref, RefObject } from "react";

function isRefObject<T>(ref: Ref<T> | null | undefined): ref is RefObject<T> {
  return ref != null && typeof ref !== "function";
}

function setRef<T>(ref: Ref<T> | null | undefined, instance: T | null): void {
  if (isRefObject(ref)) {
    // see https://github.com/facebook/react/issues/13029
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- gotta violate the readonly here :(
    (ref as any).current = instance;
  } else {
    ref?.(instance);
  }
}

/**
 * Combines multiple refs into one. Returns a ref callback
 *
 * Useful for when you need to use a forwarded ref locally as well.
 */
export function composeRefs<T>(
  ...refs: Array<Ref<T> | null | undefined>
): { bivarianceHack: (instance: T | null) => void }["bivarianceHack"] {
  return (instance: T | null) => {
    // on invoking the composite ref, we need to set each sub ref
    for (const ref of refs) {
      setRef(ref, instance);
    }
  };
}

/**
 * Combines multiple refs into one. Returns a stable ref callback
 *
 * Useful for when you need to use a forwarded ref locally as well.
 */
export function useCompositeRef<T>(
  ...refs: Parameters<typeof composeRefs<T>>
): ReturnType<typeof composeRefs<T>> {
  return React.useCallback(
    (instance: T | null) => {
      return composeRefs(...refs)(instance);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is all possible dependencies
    refs,
  );
}
